import React, { useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  User,
  Mail,
  Shield,
  MessageSquare,
  Calendar,
  Sun,
  Moon,
  Eye,
  Lock,
  Clock,
  Save,
  CheckCircle
} from 'lucide-react';
import { NotificationPreferences } from '../components/notifications/NotificationPreferences';
import { LeadSourcesManager } from '../components/settings/LeadSourcesManager';

interface DisplaySettings {
  theme: 'light' | 'dark' | 'system';
  compactView: boolean;
}

export const SettingsPage: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const { toast } = useToast();

  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Display settings state
  const [display, setDisplay] = useState<DisplaySettings>({
    theme: 'light',
    compactView: false
  });

  // Handle profile name save
  const handleSaveName = async () => {
    if (!name.trim()) {
      toast({
        title: 'Error',
        description: 'Name cannot be empty',
        variant: 'destructive'
      });
      return;
    }

    setIsSaving(true);
    try {
      const success = await updateProfile({ name: name.trim() });
      if (success) {
        toast({
          title: 'Profile Updated',
          description: 'Your name has been updated successfully'
        });
        setIsEditingName(false);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to update profile. Please try again.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle display settings
  const handleThemeChange = (theme: DisplaySettings['theme']) => {
    setDisplay(prev => ({ ...prev, theme }));
    toast({
      title: 'Theme Updated',
      description: `Theme set to ${theme}`
    });
  };

  const handleCompactViewToggle = () => {
    setDisplay(prev => {
      const newCompact = !prev.compactView;
      toast({
        title: 'Display Updated',
        description: `Compact view ${newCompact ? 'enabled' : 'disabled'}`
      });
      return { ...prev, compactView: newCompact };
    });
  };

  // Handle change password (placeholder)
  const handleChangePassword = () => {
    toast({
      title: 'Coming Soon',
      description: 'Password change functionality will be available soon'
    });
  };

  // Format role for display
  const formatRole = (role: string | undefined) => {
    if (!role) return 'Unknown';
    return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format last login
  const formatLastLogin = () => {
    // This would come from user data in a real implementation
    const now = new Date();
    return now.toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Asia/Kolkata'
    });
  };

  // Check if user can manage lead sources (CEO, GROUP_MANAGER, MANAGER)
  const canManageLeadSources = ['CEO', 'GROUP_MANAGER', 'MANAGER'].includes(user?.role || '');

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-600 mt-1">Manage your account and preferences</p>
          </div>
        </div>

        {/* Lead Sources Management - Only for admins */}
        {canManageLeadSources && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <LeadSourcesManager />
          </motion.div>
        )}

        {/* User Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                User Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Avatar and basic info */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Avatar customization coming soon</p>
                </div>
              </div>

              {/* Name field */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Name
                </label>
                {isEditingName ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="flex-1"
                      placeholder="Enter your name"
                    />
                    <Button
                      onClick={handleSaveName}
                      disabled={isSaving}
                      size="sm"
                    >
                      {isSaving ? (
                        <span className="animate-spin">...</span>
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setName(user?.name || '');
                        setIsEditingName(false);
                      }}
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-900">{user?.name || 'Not set'}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditingName(true)}
                    >
                      Edit
                    </Button>
                  </div>
                )}
              </div>

              {/* Email (read-only) */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </label>
                <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                  {user?.email || 'Not set'}
                </div>
              </div>

              {/* Role (read-only) */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Role
                </label>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    {formatRole(user?.role)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Notification Preferences Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          id="notifications"
        >
          <NotificationPreferences />
        </motion.div>

        {/* Display Settings Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-purple-600" />
                Display Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Theme selector */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-700">Theme</label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => handleThemeChange('light')}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      display.theme === 'light'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Sun className={`h-5 w-5 ${display.theme === 'light' ? 'text-blue-600' : 'text-gray-500'}`} />
                    <span className={`text-sm font-medium ${display.theme === 'light' ? 'text-blue-600' : 'text-gray-700'}`}>
                      Light
                    </span>
                    {display.theme === 'light' && (
                      <CheckCircle className="h-4 w-4 text-blue-600" />
                    )}
                  </button>
                  <button
                    onClick={() => handleThemeChange('dark')}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      display.theme === 'dark'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Moon className={`h-5 w-5 ${display.theme === 'dark' ? 'text-blue-600' : 'text-gray-500'}`} />
                    <span className={`text-sm font-medium ${display.theme === 'dark' ? 'text-blue-600' : 'text-gray-700'}`}>
                      Dark
                    </span>
                    {display.theme === 'dark' && (
                      <CheckCircle className="h-4 w-4 text-blue-600" />
                    )}
                  </button>
                  <button
                    onClick={() => handleThemeChange('system')}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      display.theme === 'system'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex">
                      <Sun className={`h-4 w-4 ${display.theme === 'system' ? 'text-blue-600' : 'text-gray-500'}`} />
                      <Moon className={`h-4 w-4 -ml-1 ${display.theme === 'system' ? 'text-blue-600' : 'text-gray-500'}`} />
                    </div>
                    <span className={`text-sm font-medium ${display.theme === 'system' ? 'text-blue-600' : 'text-gray-700'}`}>
                      System
                    </span>
                    {display.theme === 'system' && (
                      <CheckCircle className="h-4 w-4 text-blue-600" />
                    )}
                  </button>
                </div>
                <p className="text-sm text-gray-500">
                  Theme switching functionality coming soon
                </p>
              </div>

              {/* Compact view toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-3">
                  <Eye className="h-5 w-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">Compact View</p>
                    <p className="text-sm text-gray-500">Show more items in lists</p>
                  </div>
                </div>
                <button
                  onClick={handleCompactViewToggle}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    display.compactView ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      display.compactView ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Account Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-red-600" />
                Account
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Change password */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Lock className="h-5 w-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">Password</p>
                    <p className="text-sm text-gray-500">Change your account password</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleChangePassword}
                >
                  Change Password
                </Button>
              </div>

              {/* Last login info */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">Last Login</p>
                    <p className="text-sm text-gray-500">{formatLastLogin()}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};
